// ============================================================
// shared/schemas.js — le contrat unique entre le back et le front.
// ------------------------------------------------------------
// Le backend s'en sert pour VALIDER ce qu'il reçoit. Le frontend
// s'en sert pour savoir ce qu'il doit envoyer. Un seul endroit,
// donc pas de dérive possible entre les deux.
//
// Tous les objets sont STRICTS : un champ inconnu fait échouer la
// validation, il n'est pas ignoré en silence. Chez AGC la validation
// était décorative sur la moitié des champs, et un `looseObject`
// laissait passer n'importe quoi jusqu'à la base.
//
// Ces schémas valident la FORME, pas la complétude métier. Une pièce
// sans marge passe ici et sera refusée par computePiece avec un
// warning de niveau `error` : c'est le moteur de prix qui tranche ce
// qui est chiffrable, pas le validateur d'entrée.
// ============================================================

import { z } from 'zod';
import { ROUNDING_RULES, SUBCONTRACTING_MODES, RAW_SHAPES } from './pricing.js';
import { SOURCES_MATIERE, DENSITE_MIN, DENSITE_MAX } from './matiere.js';
import { UNITES_GAMME, CATEGORIES_GAMME, UNITES_PAR_CATEGORIE } from './gamme.js';
import { coteLueListe } from './aiSchemas.js';

// ── Briques réutilisables ───────────────────────────────────

/** Les colonnes NUMERIC reviennent en chaînes du driver pg, et les
 *  champs de formulaire arrivent en chaînes du navigateur. On accepte
 *  les deux et on normalise en nombre — une seule fois, ici. */
const numeric = ({ min = 0, max = undefined, int = false } = {}) => {
  let base = z.coerce.number().finite();
  if (int) base = base.int();
  if (min !== undefined) base = base.min(min);
  if (max !== undefined) base = base.max(max);
  return base;
};

/** Un montant ou une durée optionnels : `null` et `''` valent « absent ». */
const optionalNumeric = (opts) =>
  z
    .union([z.literal(''), z.null(), z.undefined(), numeric(opts)])
    .transform((v) => (v === '' || v === null || v === undefined ? null : v));

const trimmed = (max = 255) => z.string().trim().max(max);
const optionalText = (max = 255) =>
  z
    .union([z.literal(''), z.null(), z.undefined(), trimmed(max)])
    .transform((v) => (v === '' || v === undefined ? null : v));

const id = () => z.coerce.number().int().positive();
const optionalId = () =>
  z
    .union([z.literal(''), z.null(), z.undefined(), id()])
    .transform((v) => (v === '' || v === undefined ? null : v));

const uuid = () => z.string().uuid();

// ── Énumérations, alignées sur les CHECK de la base ─────────

export const ROLES = ['admin', 'user'];
export const DEVIS_STATUSES = ['draft', 'sent', 'pending', 'won', 'production', 'lost', 'expired'];

/**
 * Les statuts, UN vocabulaire pour tout le programme — 6 octobre 2026.
 * « Production » : vendu ET en atelier ; il vient après « accepté » et se
 * comporte comme lui. « Enregistré » n'est PAS un statut (c'est
 * `enregistre_at`, un geste daté — §5).
 */
export const LIBELLES_STATUT = {
  draft: 'Brouillon',
  sent: 'Envoyé',
  pending: 'En attente',
  won: 'Accepté',
  production: 'En production',
  lost: 'Perdu',
  expired: 'Expiré',
};
/** Un devis remis ou vendu ne se modifie plus : on en fait une révision. */
export const STATUTS_VERROUILLES = ['sent', 'won', 'production'];
/** Ce qui compte comme une affaire GAGNÉE (historique des prix, couche 1). */
export const STATUTS_VENDUS = ['won', 'production'];
export const OPERATION_SOURCES = ['manual', 'plan', 'ai', 'history'];
export const MATERIAL_FORMAT_KINDS = ['bar_round', 'bar_square', 'plate', 'tube'];
export const MACHINE_STATUSES = ['active', 'maintenance', 'inactive'];

/** Un traitement est fait par Mertens, ou acheté à l'extérieur. */
export const TREATMENT_NATURES = ['interne', 'sous_traite'];

/**
 * Les types de machine, en énumération FERMÉE.
 *
 * C'est l'agent 2 qui les lit pour décider d'un nombre de prises. Un
 * type libre laisserait entrer « tour » et « Tour CN » comme deux
 * choses différentes, et le raisonnement s'écroulerait en silence.
 */
export const MACHINE_TYPES = [
  'tour_cn',
  'tour_conventionnel',
  'tour_fraiseur',
  'centre_3axes',
  'centre_4axes',
  'centre_5axes',
  'rectifieuse',
  'scie',
  'presse',
  'soudure',
  'sablage_microbillage',
  'tribofinition',
  'mortaiseuse',
  'frettage',
  'metrologie',
  // Le travail à l'établi, hors machine (27 octobre 2026).
  'poste_manuel',
];
export const CATALOG_SOURCES = ['manual', 'ai', 'plan'];

export { ROUNDING_RULES, SUBCONTRACTING_MODES, RAW_SHAPES };

// ── Authentification ────────────────────────────────────────

export const loginSchema = z
  .object({
    username: z.string().trim().min(1).max(80),
    // Pas de contrainte de complexité à la connexion : elle appartient
    // au changement de mot de passe. Ici, la borne haute évite qu'un
    // corps de 1 Mo parte dans le calcul argon2.
    password: z.string().min(1).max(200),
  })
  .strict();

export const changePasswordSchema = z
  .object({
    current_password: z.string().min(1).max(200),
    new_password: z
      .string()
      .min(12, 'Douze caractères minimum.')
      .max(200)
      .refine((v) => /[a-z]/.test(v) && /[A-Z]/.test(v) && /[0-9]/.test(v), {
        message: 'Au moins une minuscule, une majuscule et un chiffre.',
      }),
  })
  .strict();

export const userCreateSchema = z
  .object({
    username: z
      .string()
      .trim()
      .min(3)
      .max(80)
      .regex(/^[a-zA-Z0-9._-]+$/, 'Lettres, chiffres, point, tiret et souligné uniquement.'),
    display_name: trimmed(120).min(1),
    password: changePasswordSchema.shape.new_password,
    role: z.enum(ROLES).default('user'),
    active: z.boolean().default(true),
  })
  .strict();

export const userUpdateSchema = userCreateSchema
  .omit({ username: true, password: true })
  .partial()
  .strict();

// ── Clients ─────────────────────────────────────────────────

export const clientSchema = z
  .object({
    company: trimmed(200).min(1, 'La société est obligatoire.'),
    contact: optionalText(120),
    email: z
      .union([z.literal(''), z.null(), z.undefined(), z.string().trim().email().max(200)])
      .transform((v) => (v === '' || v === undefined ? null : v)),
    phone: optionalText(60),
    client_number: optionalText(40),
    address: optionalText(300),
    postal_code: optionalText(20),
    city: optionalText(120),
    country: optionalText(2).default('BE'),
    vat: optionalText(30),
    discount_pct: optionalNumeric({ min: 0, max: 100 }).default(0),
    payment_terms: optionalText(200),
    notes: optionalText(4000),
    active: z.boolean().default(true),
  })
  .strict();

export const clientUpdateSchema = clientSchema.partial().strict();

// ── Gamme, paliers, sous-traitance ──────────────────────────

/**
 * Une PRISE — un montage sur une machine.
 *
 * Proposée par l'agent 2, affichée en LECTURE SEULE comme justification
 * du nombre de montages. Elle n'entre dans aucun calcul : les trois
 * totaux de la pièce font foi (§7.2). C'est `raison_existence` qui
 * compte — une phrase qui rend la proposition vérifiable en deux
 * secondes et corrigeable d'une façon qui apprend.
 */
export const piecePriseSchema = z
  .object({
    id: optionalId(),
    machine_id: optionalId(),
    /** Un transfert en contre-broche n'a pas le prix d'un montage. */
    type: z.enum(['montage_manuel', 'transfert_automatique']).default('montage_manuel'),
    raison_existence: trimmed(600).min(1),
    entites_traitees: z.array(trimmed(200)).max(60).default([]),
    cycle_estime_min: optionalNumeric({ min: 0 }),
    reglage_estime_min: optionalNumeric({ min: 0 }),
  })
  .strict();

/**
 * Une LIGNE DE GAMME — §8.2.
 *
 * ── Pourquoi le couple (catégorie, unité) est validé ICI aussi ──
 *
 * La contrainte `CHECK` de `piece_gamme` le refuse déjà en base, et
 * c'est elle qui fait foi. Mais un rejet de contrainte remonte en 500
 * avec un message Postgres : le deviseur verrait « une erreur est
 * survenue » là où il a simplement mis un montage en `par_piece`.
 *
 * Ce schéma existe pour que l'erreur soit NOMMÉE avant d'atteindre la
 * base — pas pour remplacer la contrainte. Les deux disent la même
 * chose, et le test de la contrainte se fait en base.
 */
export const pieceGammeSchema = z
  .object({
    id: optionalId(),
    /** De 10 en 10, pour pouvoir intercaler. Repris tel quel. */
    op_num: numeric({ min: 0, int: true }),
    prise_num: optionalNumeric({ min: 0, int: true }),
    machine_id: optionalId(),
    operation_id: optionalId(),
    libelle: optionalText(200),
    detail: optionalText(1000),
    /** Les usinages traités dans cette opération — « 4 × Ø6,5 ». */
    entites: z.array(trimmed(200)).max(60).default([]),
    /**
     * Un temps est un NOMBRE, jamais null.
     *
     * Zéro est autorisé et c'est une valeur : un transfert automatique
     * peut coûter zéro minute. Un `null` se lirait comme zéro dans une
     * addition, sans que personne l'ait décidé.
     */
    temps_min: numeric({ min: 0 }),
    /**
     * CE QUE L'AGENT AVAIT PROPOSÉ sur cette ligne.
     *
     * `null` sur une opération ajoutée à la main et sur toute gamme
     * antérieure au 7 septembre 2026 : ce n'est pas zéro, c'est
     * « personne ne l'a proposée ». L'écran n'affiche alors rien.
     */
    temps_min_ia: optionalNumeric({ min: 0 }),
    unite: z.enum(UNITES_GAMME),
    categorie: z.enum(CATEGORIES_GAMME),
    justification: optionalText(600),
    source: z.enum(['ia', 'corrige', 'consigne']).default('ia'),
    /**
     * La part de PROGRAMMATION contenue dans un « prototype ». C'est
     * elle, et elle seule, qu'une relance retire.
     */
    programmation_incluse_min: optionalNumeric({ min: 0 }),
    /**
     * La trace des règles CHIFFRÉES qui ont modifié cette ligne (assistant,
     * étape 3) : quelle règle, de combien à combien. Écrite par le
     * serveur, renvoyée telle quelle par l'écran, réécrite à
     * l'enregistrement — sinon le premier enregistrement l'effacerait.
     */
    effets: z.array(z.object({
      regle_id: z.number().int().positive(),
      texte: z.string().max(300),
      avant: z.number(),
      apres: z.number(),
    }).strict()).max(20).nullish(),
    /**
     * ── CE QUE LE SERVEUR RENVOIE ET QU'IL IGNORE AU RETOUR ──
     *
     * Le front réenvoie la pièce TELLE QU'IL L'A REÇUE. Ces colonnes
     * viennent du `SELECT`, donc elles reviennent — et un schéma strict
     * qui ne les connaît pas fait tomber l'ENREGISTREMENT DU DEVIS
     * ENTIER en 400, avec un message qui accuse l'écran alors que la
     * donnée vient du serveur.
     *
     * Constaté le 12 août 2026 sur AFU00498 : `total_ht` NUL en base
     * après une matinée de travail. C'est le même motif que le
     * `margin_pct` du §11 ligne 15, et que les trois champs de
     * géométrie corrigés le même jour.
     *
     * Elles sont donc ACCEPTÉES et jetées : `replaceAll` écrit sur une
     * liste blanche de colonnes, rien de ceci n'atteint la base.
     */
    piece_id: z.unknown().optional(),
    created_at: z.unknown().optional(),
    updated_at: z.unknown().optional(),
  })
  .strict()
  .superRefine((op, ctx) => {
    const autorisees = UNITES_PAR_CATEGORIE[op.categorie] ?? [];
    if (!autorisees.includes(op.unite)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['unite'],
        message:
          `Une opération de ${op.categorie} se compte en ${autorisees.join(' ou ')}, `
          + `pas en ${op.unite}. `
          + (op.categorie === 'montage'
            ? 'Un montage ne se multiplie jamais par la quantité : c’est la première pièce qui le paie.'
            : op.categorie === 'programmation'
              ? 'La programmation n’intervient qu’une fois par projet — pas par pièce, pas même par lot.'
              : ''),
      });
    }
  });

export const pieceQuantitySchema = z
  .object({
    qty: numeric({ min: 1, int: true }),
    // Le palier n'est qu'une quantité : son prix est TOUJOURS recalculé
    // par la même fonction que la pièce, en ne changeant que la qté.
    id: z.unknown().optional(),
    piece_id: z.unknown().optional(),
    price_unit: z.unknown().optional(),
    price_total: z.unknown().optional(),
    cost_unit: z.unknown().optional(),
    created_at: z.unknown().optional(),
  })
  .strict()
  .transform(({ qty }) => ({ qty }));

export const pieceSubcontractingSchema = z
  .object({
    id: optionalId(),
    sort_order: numeric({ min: 0, int: true }).default(0),
    supplier_id: optionalId(),
    label: trimmed(200).min(1, 'La sous-traitance doit porter un libellé.'),
    // Rattachement au catalogue des traitements. Facultatif : une ligne
    // ponctuelle reste possible, elle garde alors son seul libellé.
    treatment_id: optionalId(),
    /** Voir `treatmentSchema.nature`. Décide de la façon de chiffrer. */
    nature: z.enum(TREATMENT_NATURES).default('sous_traite'),
    /** Minutes par pièce, quand la ligne est INTERNE et au temps. */
    temps_min: optionalNumeric({ min: 0 }),
    taux_type: z.union([z.null(), z.enum(['machine', 'humain'])]).default(null),
    mode: z.enum(SUBCONTRACTING_MODES),
    // Nullable, comme les taux d'une opération : c'est `computePiece`
    // qui refuse une sous-traitance sans prix, avec un message qui
    // désigne la ligne fautive. Exiger la valeur ici empêcherait
    // d'enregistrer un devis en cours de saisie ; la remplacer par 0
    // ferait passer un traitement non chiffré pour un traitement
    // gratuit. Voir la migration 003.
    unit_price: optionalNumeric({ min: 0 }),
    // 0 est ici une vraie valeur : pas de minimum de facturation.
    min_invoice: optionalNumeric({ min: 0 }).default(0),
    purchase_coef: optionalNumeric({ min: 0 }),
    lead_days: optionalNumeric({ min: 0, int: true }),
    piece_id: z.unknown().optional(),
    created_at: z.unknown().optional(),
    updated_at: z.unknown().optional(),
  })
  .strict()
  .transform(({ piece_id, created_at, updated_at, ...sc }) => sc);

// ── Géométrie ───────────────────────────────────────────────

/**
 * Une ENTITÉ USINÉE, telle que `planProposal` la construit et telle que
 * `shared/geometry.js` la consomme.
 *
 * ── Trois champs manquaient, et ça BLOQUAIT TOUT ──
 *
 * `angle`, `diameter2` et `traversant` étaient écrits par l'agent et
 * lus par le calcul de volume — mais absents de ce schéma STRICT. Un
 * devis dont une pièce portait un filetage, un fraisurage ou un perçage
 * traversant était donc refusé en **400 à l'enregistrement**, entier,
 * avec un message qui accusait l'écran (« retirez-le de l'envoi »)
 * alors que la donnée venait du serveur.
 *
 * Constaté le 12 août 2026 sur AFU00498 : `total_ht` NUL en base après
 * une matinée de travail — le devis n'avait jamais pu être enregistré.
 * Et c'est la même faute que le `margin_pct` du §11, ligne 15 : un
 * champ vit dans la donnée sans vivre dans le contrat, et c'est
 * l'écriture entière qui tombe.
 *
 * Les trois sont légitimes et nécessaires : un chanfrein a un ANGLE, un
 * fraisurage un SECOND DIAMÈTRE, et un perçage TRAVERSANT prend
 * l'épaisseur de la pièce au lieu d'une profondeur écrite nulle part.
 */
export const geometryFeatureSchema = z
  .object({
    kind: optionalText(60),
    diameter: optionalNumeric({ min: 0 }),
    /** Le grand diamètre d'un fraisurage — le petit est `diameter`. */
    diameter2: optionalNumeric({ min: 0 }),
    l: optionalNumeric({ min: 0 }),
    w: optionalNumeric({ min: 0 }),
    depth: optionalNumeric({ min: 0 }),
    /** En degrés. 90 pour un fraisurage courant, 45 pour un chanfrein. */
    angle: optionalNumeric({ min: 0, max: 360 }),
    /** Traversant : la profondeur est celle de la pièce, pas du plan. */
    traversant: z.union([z.boolean(), z.null()]).optional(),
    count: optionalNumeric({ min: 1, int: true }).default(1),
  })
  .strict();

export const geometrySchema = z
  .object({
    /**
     * ── NULLABLES DEPUIS LE 22 AOÛT 2026, ET C'EST NÉCESSAIRE ──
     *
     * Une géométrie peut désormais exister SANS volume calculable : elle
     * porte alors les cotes LUES et rien d'autre. C'est le cas d'une
     * pièce dont une cote n'a pas trouvé sa case — « Bloc à souder » du
     * devis 048.
     *
     * Les laisser obligatoires ferait tomber l'enregistrement du DEVIS
     * ENTIER en 400 sur une géométrie que le serveur vient lui-même
     * d'écrire : c'est le motif du §8, et il a déjà coûté une matinée de
     * travail sur AFU00498.
     */
    partShape: z.enum(['box', 'cylinder']).nullable().default(null),
    part: z
      .object({
        diameter: optionalNumeric({ min: 0 }),
        length: optionalNumeric({ min: 0 }),
        l: optionalNumeric({ min: 0 }),
        w: optionalNumeric({ min: 0 }),
        h: optionalNumeric({ min: 0 }),
      })
      .strict()
      .nullable()
      .default(null),
    features: z.array(geometryFeatureSchema).max(200).default([]),
    /**
     * Ce que l'agent a LU sur le plan, conservé tel quel.
     *
     * C'est une PREUVE DE LECTURE : elle doit survivre à l'enregistrement,
     * sinon elle ne prouve rien au deuxième chargement de l'écran. Elle
     * n'entre dans aucun calcul — le volume se calcule sur `part`, et
     * uniquement sur lui.
     */
    /**
     * ── LA MÊME borne, dans un SECOND fichier — 25 août 2026 ──
     *
     * J'avais écrit `.max(20)` ici ET dans `aiSchemas.js`. Corriger la
     * revalidation de l'agent laissait donc celle-ci intacte : le serveur
     * RENDAIT une pièce avec 24 cotes lues et REFUSAIT la même pièce au
     * retour, en 400, sur le devis 2026-08-055.
     *
     * C'est le motif du §8 — *ce que le serveur rend, il doit l'accepter*
     * — et la démonstration qu'une règle recopiée à deux endroits n'est
     * pas une règle : c'est deux règles, dont une qu'on oublie.
     *
     * `coteLueListe` est donc la SEULE définition, importée des deux
     * côtés. Elle est incapable de refuser (voir `aiSchemas.js`).
     */
    cotes_lues: coteLueListe,
    /**
     * Les VUES relevées par l'agent d'encombrement, avec leurs deux
     * extrémités hors-tout et la ligne de cote d'où chacune vient.
     *
     * Preuve de lecture, comme `cotes_lues` : n'entre dans aucun calcul,
     * et ne peut donc jamais faire échouer un enregistrement. Tolérant
     * pour la même raison — le §8 vaut ici aussi, le serveur rend ce
     * champ et doit l'accepter au retour.
     */
    vues: z
      .preprocess((v) => (Array.isArray(v) ? v : []), z.array(z.unknown()))
      .default([])
      .transform((brut) => brut
        .map((v) => (v && typeof v === 'object' ? {
          nom: String(v.nom ?? '').slice(0, 120),
          type: ['projection', 'isometrique', 'detail'].includes(v.type) ? v.type : 'projection',
          horizontal_mm: Number(v.horizontal_mm) > 0 ? Number(v.horizontal_mm) : null,
          vertical_mm: Number(v.vertical_mm) > 0 ? Number(v.vertical_mm) : null,
          horizontal_source: String(v.horizontal_source ?? '').slice(0, 200),
          vertical_source: String(v.vertical_source ?? '').slice(0, 200),
        } : null))
        .filter(Boolean)
        .slice(0, 12)),
  })
  .strict();

// ── Pièces ──────────────────────────────────────────────────

export const pieceSchema = z
  .object({
    id: optionalId(),
    sort_order: numeric({ min: 0, int: true }).default(0),
    name: optionalText(200),
    ref: optionalText(120),
    plan_index: optionalText(20),
    qty: numeric({ min: 1, int: true }),

    material_id: optionalId(),
    material_label: optionalText(200),
    /** L'ÉTAT lu entre parenthèses — « Saldabile », « bonificato ». Il
     *  compte pour l'usinabilité, il n'entre pas au référentiel. */
    etat_matiere: optionalText(120),
    density: optionalNumeric({ min: 0 }),
    price_per_kg: optionalNumeric({ min: 0 }),

    /** Le barreau est le défaut : l'enveloppe prismatique est toujours
     *  calculable et c'est la plus neutre. `deduireFormeBrut` ne rend
     *  plus jamais `null` — l'absence de décision n'existe plus. */
    raw_shape: z.enum(RAW_SHAPES).default('barreau'),
    raw_l: optionalNumeric({ min: 0 }),
    raw_w: optionalNumeric({ min: 0 }),
    raw_h: optionalNumeric({ min: 0 }),
    raw_diameter: optionalNumeric({ min: 0 }),
    raw_length: optionalNumeric({ min: 0 }),
    raw_format_id: optionalId(),

    // Recalculés côté serveur. Chez AGC le serveur faisait confiance au
    // finished_volume envoyé par le client sans le vérifier.
    geometry: z.union([geometrySchema, z.null()]).default(null),

    /**
     * Les QUATRE variantes de brut, précalculées — §3.3 et §3.5.
     *
     * Elles étaient écrites par le dépôt et par le repository, et
     * ABSENTES de ce schéma : `.strict()` faisait donc rejeter tout
     * enregistrement qui les renvoyait, et le front ne pouvait pas les
     * conserver. La colonne existait, la route l'écrivait, rien ne
     * pouvait l'atteindre — exactement le champ « Marge » du lot D, à
     * l'envers.
     *
     * Elles ne sont pas une saisie : le serveur les recalcule quand il
     * a de quoi. Le front les renvoie telles quelles pour que basculer
     * de forme reste instantané sur un devis rouvert.
     */
    brut_variants: z.union([z.record(z.string(), z.unknown()), z.null()]).default(null),

    is_purchase: z.boolean().default(false),
    purchase_price: optionalNumeric({ min: 0 }),
    purchase_coef: optionalNumeric({ min: 0 }),

    /** Les valeurs matière qui PRÉVALENT sur l'estimation. La première
     *  présente gagne : montant, puis prix au kilo, puis référentiel. */
    prix_kg_retenu: optionalNumeric({ min: 0 }),
    montant_matiere_retenu: optionalNumeric({ min: 0 }),
    /**
     * Le transport de la matière POUR CETTE PIÈCE.
     *
     * `shared/pricing.js` le lisait depuis toujours ; il n'existait ni
     * ici ni en base, donc il valait `undefined` à chaque calcul. Le
     * calcul attendait cette colonne, l'écran ne l'a jamais fournie.
     *
     * Défaut 0, et c'est un zéro DÉCIDÉ : « pas de transport facturé »
     * est une réponse vraie, pas une absence déguisée.
     */
    transport_matiere: optionalNumeric({ min: 0 }).default(0),
    matiere_fournie_client: z.boolean().default(false),
    /** La consigne du deviseur, en langage naturel. Jamais bloquante. */
    consigne_gamme: optionalText(4000),
    /** Le programme existe déjà : la programmation vaut zéro. */
    programmation_deja_faite: z.boolean().default(false),
    min_invoice: optionalNumeric({ min: 0 }),
    /**
     * Le coefficient de SÉCURITÉ — décision du 10 août 2026.
     *
     * Ce n'est pas le retour de la marge supprimée le 5 août : celle-là
     * était un réglage GLOBAL qui devait rester à 1, donc un piège.
     * Celui-ci vit sur la pièce, vaut 1 tant que personne ne l'écrit, et
     * s'affiche sur sa propre ligne dans la synthèse avec le montant
     * qu'il ajoute. « Ce client-là, je prends 15 % de plus » est une
     * décision par affaire, pas un tarif caché.
     */
    coefficient_securite: optionalNumeric({ min: 0 }),
    // ── Les coefficients de REVENTE, par affaire ──
    //
    // Vides, les paramètres généraux s'appliquent. Remplis, ils gagnent.
    // Bornés comme en base : `> 0` parce qu'un coefficient nul ferait un
    // prix nul, et `<= 10` parce qu'au-delà c'est une faute de frappe,
    // pas une décision commerciale.
    coefficient_matiere: optionalNumeric({ min: 0.0001, max: 10 }),
    coefficient_traitement: optionalNumeric({ min: 0.0001, max: 10 }),
    rounding_rule: z.enum(ROUNDING_RULES).default('none'),

    plan_file_id: z
      .union([z.literal(''), z.null(), z.undefined(), uuid()])
      .transform((v) => (v === '' || v === undefined ? null : v)),

    /**
     * La proposition d'agent que cette pièce applique.
     *
     * Ce n'est PAS une colonne de `pieces` : c'est un aller-retour. Le
     * serveur l'a rendue à la lecture du plan, le front la renvoie à
     * l'enregistrement, et le serveur s'en sert pour comparer ce qui
     * avait été proposé à ce qui a été retenu — la couche 2 de
     * l'apprentissage.
     *
     * Sans ce chaînon, au moment du PUT le serveur ne sait plus ce
     * qu'il avait proposé, et `ai_corrections` se remplirait de vide.
     * Enterrer la proposition dans `pieces.geometry` aurait marché et
     * l'aurait rendue invisible : elle a sa table (décision de Thibaud,
     * 3 août 2026 — migration 005).
     */
    proposition_id: optionalId(),
    /**
     * Pourquoi le deviseur a corrigé — en ses mots.
     *
     * **Facultatif, jamais bloquant.** Une correction chiffrée est un
     * point de donnée ; une phrase est une RÈGLE. « Le NLX reprend en
     * contre-broche, je ne démonte pas » vaut pour toutes les pièces
     * rotatives qui tiennent en contre-broche, pas seulement celle-ci —
     * et c'est elle que l'agent 2 relira dans six mois.
     *
     * Rendre l'explication obligatoire ferait taper n'importe quoi pour
     * passer, et détruirait le seul signal qui vaille.
     */
    correction_justification: optionalText(2000),
    /**
     * Les champs qu'un HUMAIN a touchés sur cet écran.
     *
     * Sans cette liste, le serveur déduisait les corrections d'une
     * comparaison de valeurs — et l'écran, qui affiche des valeurs par
     * défaut, en fabriquait sans que personne n'ait rien fait. Absent
     * vaut AUCUN, jamais TOUS : un signal d'apprentissage se déclare.
     *
     * Jeté à l'enregistrement : il décrit un geste, pas la pièce.
     */
    champs_touches: z.array(trimmed(80)).max(80).default([]),
    // ── LES MESSAGES DE RÉVISION, EN TRANSIT — 27 août 2026 ──
    //
    // Ils ne sont PAS une colonne : ce sont les phrases que le deviseur
    // a écrites sous la gamme pendant cette session d'édition, et elles
    // servent une fois — à donner sa justification à la correction de
    // gamme qu'on capture à l'enregistrement.
    //
    // Les stocker sur la pièce en ferait un journal que personne ne
    // relit (« pas d'historique affiché », décision du 27 août) et qui
    // repartirait au modèle à chaque estimation, mêlé à la consigne
    // d'orientation. Ce qui doit survivre survit dans
    // `ai_corrections`, rangé sous la matière, la famille et la forme.
    revisions: z.array(trimmed(2000)).max(20).default([]),
    real_cycle_time: optionalNumeric({ min: 0 }),
    notes_internal: optionalText(4000),
    notes_client: optionalText(4000),

    /**
     * Les TROIS temps de la pièce — voir `shared/pricing.js`, « Le
     * TRAVAIL ». Le cycle est par pièce, le réglage et la
     * programmation par LOT : c'est ce qui fait chuter le prix
     * unitaire en série.
     *
     * Ils remplacent la gamme ligne-à-ligne : quatre lignes et
     * vingt-quatre nombres pour une rondelle, tous vides, c'était le
     * deviseur qui faisait le travail du programme.
     */
    cycle_time_min: optionalNumeric({ min: 0 }),
    setup_time_min: optionalNumeric({ min: 0 }),
    /** Le CONTRÔLE de production, par pièce. Corrigeable à la main comme
     *  les deux autres : le §8.4 promet qu'on n'est jamais obligé de
     *  descendre dans la gamme, et ce total y échappait. */
    controle_time_min: optionalNumeric({ min: 0 }),
    programming_time_min: optionalNumeric({ min: 0 }),
    /** Les temps proposés par l'agent 2, conservés à côté des retenus :
     *  c'est leur ÉCART qui mesure l'apprentissage. */
    cycle_time_min_ia: optionalNumeric({ min: 0 }),
    setup_time_min_ia: optionalNumeric({ min: 0 }),
    controle_time_min_ia: optionalNumeric({ min: 0 }),
    programming_time_min_ia: optionalNumeric({ min: 0 }),
    /**
     * Le POURQUOI de chacun des trois temps, rendu par l'agent 2 et
     * conservé avec le devis.
     *
     * Un nombre seul est à prendre ou à laisser : le deviseur n'a rien
     * sur quoi porter un désaccord, donc il corrige en silence ou pas
     * du tout. « 4 perçages Ø6.5 à 12 s » se conteste en deux secondes,
     * et c'est cette correction-là qui vaut règle.
     *
     * Persisté, pas seulement affiché : un devis rouvert dans six mois
     * doit encore dire ce qu'on avait mis dans son cycle.
     */
    justification_cycle: optionalText(1200),
    justification_controle: optionalText(1200),

    /**
     * ── CE QUE LE SERVEUR REND ET QU'IL IGNORE AU RETOUR ──
     *
     * Le front réenvoie la pièce TELLE QU'IL L'A REÇUE : c'est son
     * modèle de travail, et c'est voulu. Ces champs viennent du
     * `SELECT` — état de vérification, jeton de fiche, référence
     * interne, cache de recherche matière — et reviennent donc dans le
     * corps.
     *
     * Un schéma strict qui ne les connaît pas fait tomber
     * L'ENREGISTREMENT DU DEVIS ENTIER en 400, avec un message qui
     * accuse l'écran (« retirez-le de l'envoi ») alors que la donnée
     * vient du serveur. Constaté le 12 août 2026 : `total_ht` NUL sur
     * AFU00498 après une matinée de travail — le devis n'avait jamais
     * pu être enregistré.
     *
     * C'est le motif du `margin_pct` du §11 ligne 15 : un champ qui vit
     * dans la donnée sans vivre dans le contrat.
     *
     * Ils sont ACCEPTÉS et JETÉS : `replaceAll` écrit sur une liste
     * blanche de colonnes, rien de ceci n'atteint la base. Les accepter
     * n'ouvre donc aucune porte — le refus, lui, fermait la seule qui
     * compte.
     */
    a_verifier: z.unknown().optional(),
    raison_verification: z.unknown().optional(),
    verifiee_par: z.unknown().optional(),
    verifiee_at: z.unknown().optional(),
    brut_surepaisseur_nulle: z.unknown().optional(),
    gamme_proposee: z.unknown().optional(),
    gamme_diff: z.unknown().optional(),
    ref_interne: z.unknown().optional(),
    sheet_token: z.unknown().optional(),
    /** Ce que le mail annonçait — écrit à la naissance de la pièce, jamais ensuite. */
    qty_mail: z.unknown().optional(),
    qty_mail_extrait: z.unknown().optional(),
    material_ref: z.unknown().optional(),
    offre_matiere: z.unknown().optional(),
    material_famille: z.unknown().optional(),
    recherche_prix_en_cours: z.unknown().optional(),
    justification_reglage: optionalText(1200),
    justification_programmation: optionalText(1200),
    /** Photographiés à l'enregistrement : un devis rouvert dans six
     *  mois doit afficher le prix qu'il portait. */
    taux_machine_applique: optionalNumeric({ min: 0 }),
    taux_humain_applique: optionalNumeric({ min: 0 }),
    prises: z.array(piecePriseSchema).max(20).default([]),
    /** LA GAMME — l'objet central. Les trois temps en dérivent. */
    gamme: z.array(pieceGammeSchema).max(200).default([]),
    /**
     * Les totaux que le deviseur a FIGÉS à la main (§8.4).
     *
     * Ce n'est pas une comparaison de valeurs : le geste se DÉCLARE.
     * Déduire « il a saisi » d'un écart entre la gamme et le champ
     * refabriquerait le onzième contrôle non discriminant — un signal
     * qui compte des corrections que personne n'a faites.
     */
    temps_saisis_a_la_main: z.array(z.enum(['cycle', 'montage', 'programmation']))
      .max(3).default([]),
    /** La géométrie calculée, les entités lues et les tolérances : de
     *  l'affichage et du contexte d'agent, jetés à l'enregistrement. */
    geometrie: z.unknown().optional(),
    entites: z.unknown().optional(),
    tolerances_critiques: z.unknown().optional(),
    /** Avertissements de l'agent 2, affichés au-dessus des temps.
     *  Jetés à l'enregistrement — ils décrivent une estimation, pas la
     *  pièce. */
    temps_avertissements: z.array(trimmed(1000)).max(20).default([]),
    quantities: z.array(pieceQuantitySchema).max(20).default([]),
    subcontracting: z.array(pieceSubcontractingSchema).max(50).default([]),

    // ── Champs calculés par le serveur ──
    // Ils sont ACCEPTÉS parce que le front renvoie naturellement ce
    // qu'il a lu (GET puis PUT), et immédiatement JETÉS par le
    // transform ci-dessous. Le client ne peut donc pas fixer un prix :
    // le serveur le recalcule toujours à partir des seules entrées.
    // Les refuser casserait l'aller-retour ; les garder laisserait un
    // prix envoyé par le client atteindre la base.
    price_unit: z.unknown().optional(),
    price_total: z.unknown().optional(),
    cost_unit: z.unknown().optional(),
    pricing_snapshot: z.unknown().optional(),
    finished_volume: z.unknown().optional(),
    devis_id: z.unknown().optional(),
    created_at: z.unknown().optional(),
    updated_at: z.unknown().optional(),
  })
  .strict()
  .transform(
    ({
      price_unit,
      price_total,
      cost_unit,
      pricing_snapshot,
      finished_volume,
      devis_id,
      created_at,
      updated_at,
      ...piece
    }) => piece,
  );

// ── Devis ───────────────────────────────────────────────────

export const devisCreateSchema = z
  .object({
    /**
     * ── LE CLIENT PEUT MANQUER — 28 août 2026 ──
     *
     * Un devis né d'un mail dont l'entreprise n'a pas été reconnue
     * existe quand même : « refuser d'inventer n'est pas refuser de
     * recevoir ». `null` est donc une valeur, pas une absence — et
     * `devisUpdateSchema` en hérite, sans quoi réenregistrer un tel
     * devis ferait tomber le devis ENTIER en 400 (§8).
     */
    client_id: id().nullish(),
    project: optionalText(200),
    client_order_ref: optionalText(120),
    delay: optionalText(120),
    validity_days: optionalNumeric({ min: 0, int: true }),
    received_at: z
      .union([z.literal(''), z.null(), z.undefined(), z.coerce.date()])
      .transform((v) => (v === '' || v === undefined ? null : v)),
    notes_internal: optionalText(4000),
    notes_client: optionalText(4000),
  })
  .strict();

export const devisUpdateSchema = devisCreateSchema
  .extend({
    shipping_label: optionalText(120),
    shipping_amount: optionalNumeric({ min: 0 }).default(0),
    vat_rate: optionalNumeric({ min: 0, max: 100 }).default(21),
    discount_pct: optionalNumeric({ min: 0, max: 100 }).default(0),

    /**
     * ── L'encart matière et transport du devis — §1.1 ──
     *
     * Ce sont les montants qui PRÉVALENT sur tout calcul. La demande
     * n°1 du RDV du 5 août, répétée cinq fois : Mertens n'est
     * responsable ni du prix ni de la disponibilité de la matière, et
     * à la commande ils redemandent toujours prix + dispo + transport
     * au fournisseur.
     *
     * Rempli, `montant_matiere_retenu` part sur l'offre quelle que soit
     * la somme des coûts matière des pièces. Vide, le calcul reprend la
     * main — d'où `optionalNumeric` et non un défaut à zéro : ici,
     * confondre « vide » et « zéro » figerait la matière à 0,00 €.
     */
    montant_matiere_retenu: optionalNumeric({ min: 0 }),
    /** Aucune valeur calculée : vide, il n'apparaît pas sur l'offre. */
    transport_matiere: optionalNumeric({ min: 0 }),
    matiere_fournisseur: optionalText(200),
    matiere_offre_ref: optionalText(120),
    matiere_offre_date: z
      .union([z.literal(''), z.null(), z.undefined(), z.coerce.date()])
      .transform((v) => (v === '' || v === undefined ? null : v)),
    /**
     * Posé par le SERVEUR à chaque enregistrement explicite, remonté par
     * le `SELECT`, donc réenvoyé par le front — accepté et jeté, comme
     * les champs en lecture seule de la pièce. Sans cette ligne, le
     * schéma strict ferait tomber l'enregistrement du devis entier en
     * 400 sur une colonne que le serveur vient lui-même d'écrire.
     */
    enregistre_at: z.unknown().optional(),
    // Le statut ne se change pas par ce chemin : il a sa propre route,
    // parce qu'un passage en « envoyé » ou en « perdu » déclenche autre
    // chose qu'une simple écriture.
    pieces: z.array(pieceSchema).max(200).default([]),
  })
  .partial()
  .strict();

export const devisStatusSchema = z
  .object({
    status: z.enum(DEVIS_STATUSES),
    lost_reason: optionalText(500),
  })
  .strict()
  .refine((v) => v.status !== 'lost' || (v.lost_reason && v.lost_reason.length > 0), {
    message: 'Un devis perdu doit porter son motif de perte.',
    path: ['lost_reason'],
  });

export const devisListQuerySchema = z
  .object({
    page: numeric({ min: 1, int: true }).default(1),
    per_page: numeric({ min: 1, max: 100, int: true }).default(25),
    status: z.enum(DEVIS_STATUSES).optional(),
    client_id: optionalId(),
    // Recherche globale, référence de pièce comprise.
    q: optionalText(120),
    sort: z.enum(['created_at', 'reference', 'total_ht', 'sent_at']).default('created_at'),
    direction: z.enum(['asc', 'desc']).default('desc'),
  })
  .strict();

// ── Référentiels ────────────────────────────────────────────

/**
 * Une machine du parc.
 *
 * **Aucun taux.** Il y en a deux, globaux, dans `app_settings.rates` —
 * un taux machine pour le cycle, un taux humain pour le réglage et la
 * programmation. Une machine ne porte donc plus de tarif, et une
 * machine mal attribuée ne peut plus fausser un prix.
 *
 * Ce qu'elle porte à la place : ce qu'elle SAIT FAIRE. `contre_broche`,
 * `outils_entraines`, `axe_c`, `nb_axes` et les capacités
 * dimensionnelles sont ce sur quoi l'agent 2 s'appuie pour trancher un
 * nombre de prises — la question qui décide du prix en petite série.
 */
/**
 * ── CE QUE LE SERVEUR REND, IL DOIT L'ACCEPTER AU RETOUR — §8 ──
 *
 * Les écrans de référentiel travaillent sur la LIGNE que le serveur
 * leur a donnée : `setEditing(m)` met `m` entier dans le formulaire, et
 * l'enregistrement le renvoie tel quel. Chaque colonne ajoutée au
 * `SELECT` sans l'être au schéma fait donc tomber la MODIFICATION en
 * 400, avec un message qui accuse l'écran alors que la donnée vient du
 * serveur.
 *
 * Constaté le 1er septembre 2026, sur le signalement de Charles
 * « impossible d'ajouter une matière » : derrière le 404 de l'URL mal
 * construite se tenait un 400 sur `id`, `price_updated_at`,
 * `created_at`, `updated_at`, `source` et `confirmed`. C'est le même
 * défaut que sur AFU00498, qui avait coûté une matinée.
 *
 * Ces champs sont donc ACCEPTÉS ET JETÉS : aucun n'appartient à la
 * liste blanche de colonnes des `update*` de `catalog.repo.js`, donc
 * aucun ne peut être écrit. `z.unknown()` ne relâche rien — il dit
 * seulement « ce champ peut arriver, il n'ira nulle part ».
 *
 * Le garde-fou est mécanique et n'a pas besoin de connaître la liste :
 * `shared/schemas.referentiels.test.js` relit les colonnes que le
 * serveur renvoie et vérifie que chaque schéma les accepte.
 */
const CHAMPS_RENDUS = {
  id: z.unknown().optional(),
  source: z.unknown().optional(),
  confirmed: z.unknown().optional(),
  created_at: z.unknown().optional(),
  updated_at: z.unknown().optional(),
  price_updated_at: z.unknown().optional(),
};

export const machineSchema = z
  .object({
    name: trimmed(120).min(1),
    subtitle: optionalText(200),
    marque: optionalText(80),
    modele: optionalText(120),
    type: z.enum(MACHINE_TYPES),

    nb_axes: optionalNumeric({ min: 1, max: 9, int: true }),
    axe_c: z.boolean().default(false),
    axe_y: z.boolean().default(false),
    axe_b: z.boolean().default(false),
    outils_entraines: z.boolean().default(false),
    /** Décisif : elle supprime la reprise sur une pièce rotative. */
    contre_broche: z.boolean().default(false),
    palpeur: z.boolean().default(false),

    diametre_max_mm: optionalNumeric({ min: 0 }),
    longueur_max_mm: optionalNumeric({ min: 0 }),
    course_x_mm: optionalNumeric({ min: 0 }),
    course_y_mm: optionalNumeric({ min: 0 }),
    course_z_mm: optionalNumeric({ min: 0 }),
    diametre_table_mm: optionalNumeric({ min: 0 }),
    charge_max_kg: optionalNumeric({ min: 0 }),
    passage_barre_mm: optionalNumeric({ min: 0 }),
    nb_outils: optionalNumeric({ min: 0, int: true }),
    capacites_notes: optionalText(2000),

    status: z.enum(MACHINE_STATUSES).default('active'),
    sort_order: numeric({ min: 0, int: true }).default(0),
  })
  .strict()
  .extend(CHAMPS_RENDUS);

export const operationSchema = z
  .object({
    code: optionalText(40),
    label: trimmed(200).min(1),
    // Mêmes alias que sur les matières, et pour la même raison : ce
    // catalogue se remplit tout seul depuis les plans, donc il dérive.
    // « Alésage Ø40 H7 », « Alesage 40H7 », « ALESAGE Ø 40 » sont une
    // seule opération, et la fusion doit garder la trace des variantes
    // rencontrées — sinon l'agent recrée la ligne au plan suivant.
    aliases: z.array(trimmed(200)).max(50).default([]),
    default_machine_id: optionalId(),
    description: optionalText(1000),
    sort_order: numeric({ min: 0, int: true }).default(0),
    active: z.boolean().default(true),
    source: z.enum(CATALOG_SOURCES).default('manual'),
    confirmed: z.boolean().default(true),
  })
  .strict()
  .extend(CHAMPS_RENDUS);

/**
 * Un traitement au catalogue.
 *
 * Ce qui le distingue d'une opération d'usinage : un MODE de
 * facturation et un fournisseur, parce que c'est un achat de service.
 * Ce qu'il n'a PAS, délibérément : de prix par défaut. Un prix qui
 * apparaît tout seul sur un devis est un prix que personne n'a décidé.
 */
export const treatmentSchema = z
  .object({
    code: optionalText(40),
    label: trimmed(200).min(1),
    aliases: z.array(trimmed(200)).max(50).default([]),
    /**
     * Interne ou sous-traité — et ce n'est pas un détail de rangement.
     *
     * **Interne** : Mertens le fait. Ça coûte du TEMPS × TAUX, sur une
     * machine du parc, exactement comme une opération d'usinage.
     * **Sous-traité** : quelqu'un d'autre le fait. Ça a un PRIX, et ça
     * porte la marge commerciale.
     *
     * Le défaut d'un traitement inconnu est `sous_traite` : c'est le
     * choix prudent, il rend la pièce visiblement incomplète au lieu de
     * lui inventer un temps interne.
     */
    nature: z.enum(TREATMENT_NATURES).default('sous_traite'),
    /** La machine du parc qui le réalise, quand il est interne. */
    machine_id: optionalId(),
    /**
     * Quel taux s'applique quand le mode est `per_time`.
     *
     * C'est le TRAITEMENT qui le sait, pas une règle globale : la
     * tribofinition tourne sans personne, le microbillage est un
     * opérateur devant une cabine, la rectification est les deux.
     */
    taux_type: z.union([z.null(), z.enum(['machine', 'humain'])]).default(null),
    /** Minutes par pièce, quand il est interne. Nul = à saisir. */
    temps_defaut_min: optionalNumeric({ min: 0 }),
    default_mode: z.enum(SUBCONTRACTING_MODES).default('flat'),
    default_supplier_id: optionalId(),
    description: optionalText(1000),
    sort_order: numeric({ min: 0, int: true }).default(0),
    active: z.boolean().default(true),
    source: z.enum(CATALOG_SOURCES).default('manual'),
    confirmed: z.boolean().default(true),
  })
  .strict()
  .extend(CHAMPS_RENDUS);

export const materialSchema = z
  .object({
    designation: trimmed(200).min(1),
    norm: optionalText(120),
    /**
     * La densité, BORNÉE — invariant 9, sur la surface d'ÉCRITURE.
     *
     * Le garde-fou `jugerDensite` existait depuis le lot D et s'exerçait
     * à la LECTURE seulement. Le 6 août, « Aluminium 5083 » est entré au
     * référentiel avec **7340** : des kg/m³ tapés dans un champ en
     * g/cm³, par un humain, à travers cette route — un facteur mille que
     * `min: 0` acceptait sans un mot.
     *
     * Une valeur refusée ici ne coûte rien : elle se retape. Une valeur
     * fausse acceptée voyage jusqu'à la masse, donc au coût matière, et
     * personne ne la retrouve.
     */
    density: optionalNumeric({ min: DENSITE_MIN, max: DENSITE_MAX }),
    price_per_kg: optionalNumeric({ min: 0 }),
    category: optionalText(120),
    aliases: z.array(trimmed(120)).max(50).default([]),
    /**
     * Indice d'usinabilité, base 100 = 11SMnPb30 (décolletage).
     *
     * Il n'entre dans AUCUN calcul de prix. Il sert à la signature de
     * pièce — rapprocher deux pièces comparables — et au raisonnement
     * de l'agent 2 : usiner du titane à 20 et de l'aluminium à 250
     * n'est pas le même métier, ni le même temps.
     */
    usinabilite: optionalNumeric({ min: 1, max: 1000, int: true }),
    /**
     * Le prix a-t-il été confirmé par Charles ?
     *
     * Les prix amorcés au référentiel sont des ORDRES DE GRANDEUR du
     * marché belge, pas les prix de son fournisseur. Tant que ce
     * drapeau est faux, la pièce chiffrée dessus porte un
     * avertissement — un prix indicatif présenté comme ferme produit un
     * devis d'apparence sûre, envoyé, et engageant.
     */
    price_confirmed: z.boolean().default(false),
    active: z.boolean().default(true),
    source: z.enum(CATALOG_SOURCES).default('manual'),
    confirmed: z.boolean().default(true),

    /**
     * D'où viennent la densité et le prix — SÉPARÉMENT (§2.5).
     *
     * Ils ne sont pas de la même nature : une densité est une constante
     * physique, un prix est volatil. Une seule colonne de provenance
     * forcerait à choisir laquelle des deux elle décrit, et le
     * référentiel afficherait une pastille verte sur un prix web parce
     * que la densité, elle, est sûre.
     */
    density_source: z.enum(SOURCES_MATIERE).nullish(),
    price_source: z.enum(SOURCES_MATIERE).nullish(),
    source_url: optionalText(2000),
  })
  .strict()
  .extend(CHAMPS_RENDUS);

/**
 * Une offre fournisseur reçue pour une nuance — §1.5.
 *
 * C'est la meilleure source de prix qui existe : un vrai prix, chez un
 * vrai fournisseur, à une vraie date. Elle bat le référentiel dans la
 * cascade du §2.1, et au bout de quelques mois elle rend la recherche
 * en ligne inutile.
 *
 * Trois champs seulement sont obligatoires — nuance, date, prix. Déposer
 * une offre doit prendre dix secondes, sinon personne ne le fait et la
 * table reste vide, comme le catalogue d'opérations saisi à la main que
 * le §7 refuse pour exactement la même raison.
 */
export const offreMatiereSchema = z
  .object({
    material_id: id(),
    fournisseur: optionalText(200),
    date_offre: z.coerce.date(),
    prix_kg: numeric({ min: 0 }).refine((v) => v > 0, {
      message: 'Une offre à 0 €/kg n’est pas une offre.',
    }),
    quantite_offerte: optionalText(120),
    forme: optionalText(120),
    reference_offre: optionalText(120),
    file_id: z
      .union([z.literal(''), z.null(), z.undefined(), uuid()])
      .transform((v) => (v === '' || v === undefined ? null : v)),
    devis_id: optionalId(),
    note: optionalText(2000),
  })
  .strict();

export const materialFormatSchema = z
  .object({
    material_id: id(),
    label: optionalText(200),
    kind: z.enum(MATERIAL_FORMAT_KINDS),
    dimensions: z.record(z.string(), numeric({ min: 0 })).default({}),
    stock_length: optionalNumeric({ min: 0 }),
    kerf_mm: optionalNumeric({ min: 0 }).default(0),
    price_per_kg: optionalNumeric({ min: 0 }),
    min_order: optionalNumeric({ min: 0 }),
    active: z.boolean().default(true),
  })
  .strict()
  .extend(CHAMPS_RENDUS);

export const supplierSchema = z
  .object({
    name: trimmed(200).min(1),
    contact: optionalText(120),
    email: clientSchema.shape.email,
    phone: optionalText(60),
    lead_days: optionalNumeric({ min: 0, int: true }),
    notes: optionalText(4000),
    active: z.boolean().default(true),
  })
  .strict()
  .extend(CHAMPS_RENDUS);

// ── Paramètres ──────────────────────────────────────────────

export const pricingSettingsSchema = z
  .object({
    /**
     * Les trois COEFFICIENTS DE REVENTE.
     *
     * Il n'y a plus de marge : le taux horaire est un taux de vente,
     * elle est dedans. Ces trois-là portent ce que Mertens gagne sur ce
     * qu'il ACHÈTE et refacture — et rien sur l'usinage.
     *
     * Le transport est à 1,00 par défaut : rebillé au coût.
     */
    coefficient_matiere: optionalNumeric({ min: 0 }),
    coefficient_sous_traitance: optionalNumeric({ min: 0 }),
    coefficient_transport: optionalNumeric({ min: 0 }),
    coefficients_confirmes: z.boolean().optional(),
    /** Les surépaisseurs de brut, réglables PAR FORME. */
    surepaisseurs_par_forme: z.unknown().optional(),
    default_min_invoice: optionalNumeric({ min: 0 }),
    default_rounding: z.enum(ROUNDING_RULES),
  })
  .strict();

/**
 * Les taux — DEUX, et deux seulement.
 *
 * `machine_rate` s'applique au TEMPS DE CYCLE ; `human_rate` au
 * RÉGLAGE et à la PROGRAMMATION. Aucune machine ne porte de taux.
 *
 * La conséquence est le cœur du modèle de prix : le taux humain étant
 * le plus élevé, **une minute de réglage coûte plus cher qu'une minute
 * d'usinage**. Le nombre de prises devient donc le premier levier du
 * prix en petite série — et c'est là-dessus que l'agent 2 se juge.
 *
 * `rates_confirmed` reste `false` tant que Charles n'a pas validé les
 * deux valeurs. Tant qu'il vaut `false`, chaque pièce chiffrée porte un
 * avertissement : un prix bâti sur un taux non confirmé n'est pas un
 * prix, c'est une estimation.
 */
export const ratesSettingsSchema = z
  .object({
    machine_rate: optionalNumeric({ min: 0 }),
    human_rate: optionalNumeric({ min: 0 }),
    rates_confirmed: z.boolean().default(false),
    vat_rates: z.array(numeric({ min: 0, max: 100 })).min(1),
    default_vat_rate: numeric({ min: 0, max: 100 }),
    validity_days: numeric({ min: 0, int: true }),
  })
  .strict();

/**
 * Coordonnées de l'atelier, imprimées sur chaque devis.
 *
 * Les cinq premiers champs sont OBLIGATOIRES : en Belgique, un document
 * commercial porte au minimum la dénomination, l'adresse complète et le
 * numéro de TVA. La génération d'un devis est refusée s'ils manquent —
 * aucune valeur de repli sur un document qui part chez un client.
 */
export const companySettingsSchema = z
  .object({
    name: trimmed(200).min(1, "La dénomination est obligatoire sur un devis."),
    tagline: optionalText(80),
    address: trimmed(300).min(1, "L'adresse est obligatoire sur un devis."),
    postal_code: trimmed(20).min(1, 'Le code postal est obligatoire.'),
    city: trimmed(120).min(1, 'La commune est obligatoire.'),
    country: optionalText(2).default('BE'),
    vat: trimmed(30).min(1, 'Le numéro de TVA est obligatoire sur un devis.'),
    phone: optionalText(60),
    email: optionalText(200),
    iban: optionalText(40),
    // Conditions générales, mentions de réserve de propriété, etc.
    legal: optionalText(4000),
  })
  .strict();

export const numberingSettingsSchema = z
  .object({
    year: z.union([z.null(), numeric({ min: 2000, max: 3000, int: true })]),
    month: z.union([z.null(), numeric({ min: 1, max: 12, int: true })]),
    next: numeric({ min: 1, int: true }),
  })
  .strict();

/**
 * Modèle d'e-mail d'envoi d'un devis.
 *
 * `subject` et `body` acceptent quatre marques : `{{reference}}`,
 * `{{client}}`, `{{projet}}` et `{{validite}}`. Il n'y en a pas d'autres
 * et une marque inconnue est laissée telle quelle — visible dans le mail
 * plutôt que remplacée en silence par du vide, ce qui se verrait chez le
 * client. Ce n'est PAS un moteur de gabarit : pas de logique, pas de
 * boucle, pas d'accès à un objet arbitraire.
 */
export const mailSettingsSchema = z
  .object({
    subject: trimmed(300).min(1, "L'objet du mail ne peut pas être vide."),
    body: trimmed(8000).min(1, 'Le corps du mail ne peut pas être vide.'),
    // Copie systématique, typiquement la boîte de l'atelier.
    cc: optionalText(200),
    reply_to: optionalText(200),
  })
  .strict();

/** Un schéma par groupe de paramètres. Un groupe inconnu est refusé :
 *  on ne laisse pas s'installer une clé fantôme dans app_settings. */
/**
 * La LISTE BLANCHE du bot d'atelier — de la configuration, éditable.
 *
 * Elle est nécessaire parce que la référence interne d'une pièce est de
 * la forme « date + numéro », donc DEVINABLE : contrairement au jeton du
 * QR, qui est un UUID. Sans liste, quiconque trouve le bot pourrait
 * écrire des temps sur une affaire réelle.
 *
 * L'accusé de réception Telegram (`offset`) n'est PAS ici, et c'est
 * délibéré : c'est de l'état écrit par la machine. Le mettre dans le
 * même formulaire ferait qu'autoriser un opérateur le remettrait à la
 * valeur du champ — et un offset revenu en arrière fait relire tout
 * l'historique.
 */
export const telegramChatsSettingsSchema = z
  .object({
    chats: z.array(z.coerce.number().int()).max(50).default([]),
  })
  .strict();

// L'assistant (14 octobre 2026, docs/OUTIL-ASSISTANT.md §2 et §11) : les
// instructions générales, relues à chaque réponse, et le plafond de
// messages par jour et par utilisateur.
export const assistantSettingsSchema = z.object({
  instructions_generales: z.string().max(4000).optional().default(''),
  plafond_messages_jour: z.number().int().min(1).max(1000).optional().default(100),
}).strict();

export const settingsSchemas = {
  assistant: assistantSettingsSchema,
  pricing: pricingSettingsSchema,
  rates: ratesSettingsSchema,
  numbering: numberingSettingsSchema,
  company: companySettingsSchema,
  mail: mailSettingsSchema,
  telegram_chats: telegramChatsSettingsSchema,
};

// ── Règles métier ───────────────────────────────────────────

export const businessRuleSchema = z
  .object({
    scope: trimmed(80).min(1),
    // La phrase EXACTE du client. C'est elle la source de vérité, pas
    // sa reformulation. Chez AGC c'est la réécriture par l'IA qui était
    // stockée et appliquée, sans que personne puisse voir la dérive.
    input_text: z.string().trim().min(1).max(4000),
    structured: z.union([z.record(z.string(), z.unknown()), z.null()]).default(null),
    active: z.boolean().default(false),
  })
  .strict();

// ── Aide à la validation ────────────────────────────────────

/**
 * Met une erreur zod à plat, en `{ champ: message }`, prête à être
 * affichée à côté du champ fautif.
 *
 * ── Le cas des clés inconnues, et l'invariant 10 ──
 *
 * Les schémas de ce projet sont STRICTS, et ça ne change pas : un champ
 * inconnu doit faire échouer la validation, sinon la validation est
 * décorative — c'est le défaut d'AGC.
 *
 * Mais un rejet strict qui ne dit pas QUOI RETIRER est un mur. Zod rend
 * `path: ['pieces', 0]` et le message « Unrecognized key(s) in object:
 * 'margin_pct' » : le chemin désigne l'OBJET, pas le champ, et le nom du
 * coupable est enfoui dans une phrase en anglais.
 *
 * C'est exactement le cas vécu le 7 août : un champ « Marge » resté à
 * l'écran rendait le devis inenregistrable, et le message ne nommait ni
 * le champ ni où agir. On remonte donc la clé fautive à SA place —
 * `pieces.0.margin_pct` — avec une phrase qui dit quoi faire.
 */
export function formatZodError(error) {
  const fields = {};
  const pose = (chemin, message) => {
    if (!fields[chemin]) fields[chemin] = message;
  };

  for (const issue of error.issues ?? []) {
    const base = issue.path.join('.');

    if (issue.code === 'unrecognized_keys') {
      for (const cle of issue.keys ?? []) {
        pose(
          [base, cle].filter(Boolean).join('.'),
          `Champ « ${cle} » inconnu à cet endroit : retirez-le de l’envoi. `
          + 'Il a peut-être été supprimé du modèle sans l’être de l’écran.',
        );
      }
      // Et une entrée sur l'objet lui-même, pour l'appelant qui n'affiche
      // que le premier niveau : sans elle, le message resterait invisible
      // sur un écran qui ne descend pas dans les tableaux.
      pose(
        base || '_',
        `Champ${(issue.keys ?? []).length > 1 ? 's' : ''} inconnu`
        + `${(issue.keys ?? []).length > 1 ? 's' : ''} : `
        + `${(issue.keys ?? []).join(', ')}. Retirez-${(issue.keys ?? []).length > 1 ? 'les' : 'le'} de l’envoi.`,
      );
      continue;
    }

    pose(base || '_', issue.message);
  }
  return fields;
}
